import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/*
 * MYNSU speed batch 4 (10/3/2026). Two changes on top of the stock Horizon component:
 *
 * 1. Lazy markup. Product cards no longer carry the quick add markup (two buttons, a form, the "added" animation:
 *    about 5.8 KB and 52 elements per card). The server renders an empty <quick-add-component data-lazy> with the
 *    per-product values in data attributes, and the page carries the markup once, in <template id="quick-add-template">
 *    (snippets/quick-add-template.liquid). The component builds its content from that template wherever the button can
 *    actually show: 750px and up, or every width when mobile quick add is on. Below 750px the button is display:none,
 *    so nothing is built. The built DOM has the same structure as what the server used to render.
 *
 * 2. Opening the modal. The product page fetched on hover / focus is now kept and reused by the click (the stock code
 *    threw it away and fetched again), the button shows a busy state while the page is on its way, and a failed or
 *    very slow fetch falls back to a plain navigation to the product page instead of doing nothing.
 */

/** How many fetched product pages are kept for reuse. Shared by all cards; small on purpose (a parsed page is large). */
const RECENT_PAGES_LIMIT = 3;

/** @type {Map<string, Promise<Document | null>>} */
const recentPages = new Map();

/** How long a click waits for the product page before going to it instead. */
const MODAL_CONTENT_TIMEOUT = 8000;

/** The per-product values the server writes on a lazy component. */
const LAZY_ATTRIBUTES = [
  'data-lazy',
  'data-section-id',
  'data-product-id',
  'data-form-id',
  'data-variant-id',
  'data-quantity',
  'data-variant-media',
];

/**
 * Rejects if the promise has not settled in time.
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @returns {Promise<T>}
 */
function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Timed out')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

export class QuickAddComponent extends Component {
  static observedAttributes = LAZY_ATTRIBUTES;

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** True between connectedCallback and disconnectedCallback. */
  #connected = false;
  /** True while a click is waiting for the product page. */
  #loading = false;
  /** True while a re-sync of the lazy markup is queued. */
  #lazySyncQueued = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    mediaQueryLarge.addEventListener('change', this.#syncLazyMarkup);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    // Touch and pen have no hover: start fetching the product page when the press begins.
    this.addEventListener('pointerdown', this.#prefetchOnPress, { passive: true });

    this.#connected = true;
    this.#syncLazyMarkup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#connected = false;
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    mediaQueryLarge.removeEventListener('change', this.#syncLazyMarkup);
    this.removeEventListener('pointerdown', this.#prefetchOnPress);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * The server changed the per-product values of a lazy component (a section re-render morphs the attributes and,
   * because of data-skip-subtree-update, leaves the built content alone): copy the new values into the content.
   */
  attributeChangedCallback() {
    if (!this.#connected || this.#lazySyncQueued) return;

    this.#lazySyncQueued = true;
    // After the morph that changed the attributes has finished with this element.
    queueMicrotask(() => {
      this.#lazySyncQueued = false;
      this.#syncLazyMarkup();
    });
  }

  /**
   * Builds the content of a lazy component from the shared template, or refreshes the values of content already built.
   * Does nothing for components the server rendered in full, and nothing where the button cannot show.
   */
  #syncLazyMarkup = () => {
    if (!this.#connected || !this.hasAttribute('data-lazy')) return;

    const buttonCanShow = this.dataset.lazy === 'always' || mediaQueryLarge.matches || Boolean(window.Shopify?.designMode);
    if (!buttonCanShow) return;

    if (this.firstElementChild) {
      this.#fillLazyMarkup(this);
      return;
    }

    const template = document.getElementById('quick-add-template');
    if (!(template instanceof HTMLTemplateElement)) return;

    const content = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    // Fill before inserting: the components inside read these values as soon as they connect.
    this.#fillLazyMarkup(content);
    this.append(content);
  };

  /**
   * Copies the per-product values from this component's data attributes into the built content.
   * @param {ParentNode} root - The cloned template content, or this component once built.
   */
  #fillLazyMarkup(root) {
    const { sectionId = '', productId = '', formId = '', variantId = '', quantity = '1', variantMedia = '' } = this.dataset;

    /**
     * @param {string} selector
     * @param {string} name
     * @param {string} value
     */
    const set = (selector, name, value) => {
      const element = root.querySelector(selector);
      if (element && element.getAttribute(name) !== value) element.setAttribute(name, value);
    };

    set('product-form-component', 'data-section-id', sectionId);
    set('product-form-component', 'data-product-id', productId);
    set('form', 'id', formId);
    set('input[name="id"]', 'value', variantId);
    set('input[name="quantity"]', 'value', quantity);
    set('input[name="product-id"]', 'value', productId);
    set('input[name="section-id"]', 'value', sectionId);
    set('add-to-cart-component', 'data-product-variant-media', variantMedia);
  }

  /**
   * Starts fetching the product page as soon as the "Choose" button is pressed.
   * @param {PointerEvent} event
   */
  #prefetchOnPress = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.quick-add__button--choose')) return;

    this.fetchProductPage(this.productPageUrl);
  };

  /**
   * Shows or clears the busy state of the "Choose" button while a click waits for the product page.
   * @param {boolean} loading
   */
  #setLoading(loading) {
    this.#loading = loading;

    const button = this.querySelector('.quick-add__button--choose');
    if (!button) return;

    if (loading) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    recentPages.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // A click is already waiting for this product's page: one modal is enough.
    if (this.#loading) return;

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch (or pick up the page already fetched on hover / focus / press) and cache the content
      this.#setLoading(true);
      let failed = false;

      try {
        const html = await withTimeout(this.#loadProductPage(currentUrl), MODAL_CONTENT_TIMEOUT);

        // No page = the request was aborted because this card moved on to another URL: not a failure.
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');

          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          } else {
            failed = true;
          }
        }
      } catch (error) {
        // Network error, error status or timeout
        failed = true;
      } finally {
        this.#setLoading(false);
      }

      if (!productGrid) {
        // Nothing to show in the modal. Go to the product page (what a click on the card does) rather than leaving
        // the click without any effect. Not when the card itself is gone (the section was re-rendered meanwhile).
        if (failed && this.isConnected && currentUrl) window.location.href = currentUrl;
        return;
      }
    }

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content ahead of a click: called on hover / focus (product-card.js) and on press.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  fetchProductPage(productPageUrl) {
    // Only the "Choose" button opens the modal. While the card shows "Add" (it adds straight to the cart) the page
    // would be fetched and parsed for nothing on every hover.
    if (this.dataset.quickAddButton === 'add') return Promise.resolve(null);

    return this.#loadProductPage(productPageUrl);
  }

  /**
   * Fetches the product page content. A page that is already on its way, or was fetched a moment ago (hover, focus,
   * press, an earlier click), is reused instead of being requested again.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>} The page, or null if the request was aborted. Rejects on a failed request.
   */
  #loadProductPage(productPageUrl) {
    if (!productPageUrl) return Promise.resolve(null);

    const recent = recentPages.get(productPageUrl);
    if (recent) {
      // Mark as the most recently used
      recentPages.delete(productPageUrl);
      recentPages.set(productPageUrl, recent);
      return recent;
    }

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    const request = this.#requestProductPage(productPageUrl, abortController);

    recentPages.set(productPageUrl, request);
    for (const url of recentPages.keys()) {
      if (recentPages.size <= RECENT_PAGES_LIMIT) break;
      recentPages.delete(url);
    }

    // An aborted or failed request must not be reused. This also keeps a failed hover fetch quiet: the click that
    // may follow starts a new request and handles its failure itself.
    const forget = () => {
      if (recentPages.get(productPageUrl) === request) recentPages.delete(productPageUrl);
    };
    request.then((html) => {
      if (!html) forget();
    }, forget);

    return request;
  }

  /**
   * @param {string} productPageUrl
   * @param {AbortController} abortController
   * @returns {Promise<Document | null>}
   */
  async #requestProductPage(productPageUrl, abortController) {
    try {
      const response = await fetch(productPageUrl, {
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === abortController) this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
